import { useCallback, useEffect, useState } from "react";
import { fetchDeployState } from "./api";
import type { ServiceState } from "@rebuild/shared";
import { ServiceCard } from "./components/ServiceCard";
import { PasswordGate, SESSION_KEY } from "./components/PasswordGate";
import MappingTopBox from "../CreatorTools/MappingTool/components/MappingTopBox";

export default function DeployDashboardPage() {
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [states, setStates] = useState<ServiceState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadState = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchDeployState();
      setStates(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    loadState();
    const interval = setInterval(loadState, 30_000);
    return () => clearInterval(interval);
  }, [loadState, authenticated]);

  if (!authenticated) {
    return (
      <div>
        <MappingTopBox />
        <PasswordGate onAuthenticated={() => setAuthenticated(true)} />
      </div>
    );
  }

  return (
    <div>
      <MappingTopBox />
      <div className="max-w-5xl mx-auto mt-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Deploy Dashboard</h1>
          <button
            onClick={loadState}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading deployments...</div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-3">{error}</p>
            <button onClick={loadState} className="text-indigo-600 hover:text-indigo-500 text-sm">Retry</button>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-gray-500">Current deployment state across all environments</p>
            <div className="grid gap-4">
              {states.map((s) => (
                <ServiceCard key={s.service} state={s} onPromoted={loadState} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
